(function () {
  'use strict';

  function init() {
    document.querySelectorAll('.custom-image-dropdown').forEach(setupDropdown);

    document.querySelectorAll('variant-radios').forEach((radios) => {
      if (radios.dataset.customDropdownBound === '1') return;
      radios.dataset.customDropdownBound = '1';
      radios.addEventListener('change', () => {
        // Defer one tick so <variant-radios> finishes its own update first.
        setTimeout(() => syncAfterVariantChange(radios), 0);
      });
      // Initial sync (covers cases where Dawn fires no initial change event).
      setTimeout(() => syncAfterVariantChange(radios), 0);
    });
  }

  function setupDropdown(dropdown) {
    if (dropdown.dataset.bound === '1') return;
    dropdown.dataset.bound = '1';

    const trigger = dropdown.querySelector('[data-trigger]');
    const panel = dropdown.querySelector('[data-panel]');
    const items = Array.from(dropdown.querySelectorAll('.custom-image-dropdown__item'));

    if (!trigger || !panel) return;

    // Find the matching hidden radios for this option.
    const fieldset = dropdown.closest('fieldset');
    const optionName = dropdown.dataset.optionName;
    const radiosRoot = dropdown.closest('variant-radios');

    function getRadios() {
      if (!fieldset) return [];
      return Array.from(fieldset.querySelectorAll(`input[type="radio"][name="${cssEscape(optionName)}"]`));
    }

    // Set trigger to currently selected value.
    const selectedItem = items.find((i) => i.classList.contains('is-selected')) || items[0];
    updateTriggerFromItem(dropdown, selectedItem);

    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      togglePanel(dropdown);
    });

    items.forEach((item) => {
      item.addEventListener('click', (e) => {
        e.preventDefault();
        if (item.classList.contains('is-disabled')) return;
        selectItem(dropdown, item);
        const value = item.dataset.value;
        const radios = getRadios();
        const radio = radios.find((r) => r.value === value);
        if (radio) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
        closePanel(dropdown);
      });

      item.addEventListener('keydown', (e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !item.classList.contains('is-disabled')) {
          e.preventDefault();
          item.click();
        }
      });
    });

    // Click outside closes.
    document.addEventListener('click', (e) => {
      if (!dropdown.contains(e.target)) closePanel(dropdown);
    });

    // Escape closes.
    dropdown.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closePanel(dropdown);
        trigger.focus();
      }
    });
  }

  function togglePanel(dropdown) {
    const panel = dropdown.querySelector('[data-panel]');
    const trigger = dropdown.querySelector('[data-trigger]');
    const isOpen = !panel.hasAttribute('hidden');
    if (isOpen) closePanel(dropdown);
    else openPanel(dropdown);
  }

  function openPanel(dropdown) {
    const panel = dropdown.querySelector('[data-panel]');
    const trigger = dropdown.querySelector('[data-trigger]');
    panel.removeAttribute('hidden');
    trigger.setAttribute('aria-expanded', 'true');
    dropdown.classList.add('is-open');
  }

  function closePanel(dropdown) {
    const panel = dropdown.querySelector('[data-panel]');
    const trigger = dropdown.querySelector('[data-trigger]');
    panel.setAttribute('hidden', '');
    trigger.setAttribute('aria-expanded', 'false');
    dropdown.classList.remove('is-open');
  }

  function selectItem(dropdown, item) {
    dropdown.querySelectorAll('.custom-image-dropdown__item').forEach((el) => {
      el.classList.remove('is-selected');
      el.setAttribute('aria-selected', 'false');
      const check = el.querySelector('.custom-image-dropdown__item-check');
      if (check) check.remove();
    });
    item.classList.add('is-selected');
    item.setAttribute('aria-selected', 'true');
    if (!item.querySelector('.custom-image-dropdown__item-check')) {
      const check = document.createElement('span');
      check.className = 'custom-image-dropdown__item-check';
      check.setAttribute('aria-hidden', 'true');
      check.textContent = '✓';
      item.appendChild(check);
    }
    updateTriggerFromItem(dropdown, item);
  }

  function updateTriggerFromItem(dropdown, item) {
    if (!item) return;
    const trigger = dropdown.querySelector('[data-trigger]');
    const thumbEl = trigger.querySelector('[data-trigger-thumb]');
    const labelEl = trigger.querySelector('[data-trigger-label]');
    const imgSrc = item.dataset.imageSrc;
    const imgAlt = item.dataset.imageAlt;
    if (thumbEl) {
      thumbEl.innerHTML = imgSrc
        ? `<img src="${escapeAttr(imgSrc)}" alt="${escapeAttr(imgAlt || '')}" loading="lazy">`
        : '';
    }
    if (labelEl) labelEl.textContent = item.dataset.value;
  }

  function syncAfterVariantChange(radiosRoot) {
    // Read variant JSON for dynamic availability.
    let variants = [];
    try {
      const scriptEl = radiosRoot.querySelector('script[type="application/json"]');
      if (scriptEl) variants = JSON.parse(scriptEl.textContent);
    } catch (_) {
      return;
    }
    if (!variants.length) return;

    // Read current selection per option position from the radios.
    const fieldsets = Array.from(radiosRoot.querySelectorAll('fieldset'));
    const selected = fieldsets.map((fs) => {
      const radio = Array.from(fs.querySelectorAll('input[type="radio"]')).find((r) => r.checked);
      return radio ? radio.value : null;
    });

    // For each custom dropdown, recompute disabled state per item.
    radiosRoot.querySelectorAll('.custom-image-dropdown').forEach((dropdown) => {
      const pos = parseInt(dropdown.dataset.optionPosition, 10);
      const items = Array.from(dropdown.querySelectorAll('.custom-image-dropdown__item'));
      items.forEach((item) => {
        const value = item.dataset.value;
        const available = variants.some((v) => {
          if (!v.available) return false;
          if (v[`option${pos}`] !== value) return false;
          for (let i = 1; i <= 3; i++) {
            if (i === pos) continue;
            const sel = selected[i - 1];
            if (sel == null) continue;
            if (v[`option${i}`] !== sel) return false;
          }
          return true;
        });
        item.classList.toggle('is-disabled', !available);
        item.setAttribute('aria-disabled', !available ? 'true' : 'false');
        item.setAttribute('tabindex', available ? '0' : '-1');
      });

      // Update trigger to reflect the currently selected radio for this option.
      const fs = items[0] ? items[0].closest('fieldset') : null;
      if (fs) {
        const checked = Array.from(fs.querySelectorAll('input[type="radio"]')).find((r) => r.checked);
        if (checked) {
          const matchingItem = items.find((i) => i.dataset.value === checked.value);
          if (matchingItem) {
            items.forEach((el) => {
              el.classList.remove('is-selected');
              el.setAttribute('aria-selected', 'false');
              const c = el.querySelector('.custom-image-dropdown__item-check');
              if (c) c.remove();
            });
            matchingItem.classList.add('is-selected');
            matchingItem.setAttribute('aria-selected', 'true');
            if (!matchingItem.querySelector('.custom-image-dropdown__item-check')) {
              const check = document.createElement('span');
              check.className = 'custom-image-dropdown__item-check';
              check.setAttribute('aria-hidden', 'true');
              check.textContent = '✓';
              matchingItem.appendChild(check);
            }
            updateTriggerFromItem(dropdown, matchingItem);
          }
        }
      }
    });
  }

  function cssEscape(s) {
    if (window.CSS && window.CSS.escape) return window.CSS.escape(s);
    return String(s).replace(/(["\\])/g, '\\$1');
  }

  function escapeAttr(s) {
    return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
